/**
 * Local writes.
 *
 * Every mutation does exactly two things, in one Dexie transaction:
 *   1. change the local row, so the UI updates on the next frame;
 *   2. append to the outbox, so the change reaches the server eventually.
 *
 * Doing both atomically is the point. A crash between them would leave either a
 * silent edit that never syncs, or a queued mutation for a row the user cannot
 * see - both far worse than the write simply not happening.
 */
import type { SyncTable } from '@mouldtrack/shared';
import { SYNC_TABLE_MAP, db } from './schema.js';
import { enqueue } from '../sync/outbox.js';
import { refreshCounters } from '../sync/outbox.js';
import { requestSync } from '../sync/engine.js';
import { newId } from '../lib/id.js';

/** Fields the server owns. Sending them back would be noise at best. */
const SERVER_OWNED = new Set(['id', 'updatedAt', 'createdAt', 'deletedAt', '_pending']);

function stripServerFields(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (!SERVER_OWNED.has(k)) out[k] = v;
  }
  return out;
}

export interface MutationResult {
  id: string;
}

/**
 * Create a row. The ID is minted here so the new row can be referenced (and
 * navigated to) immediately, offline, before the server has heard of it.
 */
export async function createRow(
  table: SyncTable,
  data: Record<string, unknown>,
): Promise<MutationResult> {
  const id = newId();
  const payload = stripServerFields(data);
  const now = new Date().toISOString();

  await db.transaction('rw', db.table(SYNC_TABLE_MAP[table]), db.outbox, async () => {
    // Provisional `updatedAt` and `createdAt`: both replaced by the server's on
    // the next sync. They exist so the row behaves like a row before it has ever
    // been pushed - sorting works, and the record can say when it was added
    // rather than claiming it predates the system, which is how a mould created
    // ten seconds ago read.
    await db
      .table(SYNC_TABLE_MAP[table])
      .put({ ...payload, id, createdAt: now, updatedAt: now, _pending: 1 });
    await enqueue({ table, op: 'create', id, data: payload });
  });

  await refreshCounters();
  requestSync();
  return { id };
}

/** Patch a row. Only the changed fields travel; `baseUpdatedAt` arms conflict detection. */
export async function updateRow(
  table: SyncTable,
  id: string,
  patch: Record<string, unknown>,
): Promise<MutationResult> {
  const payload = stripServerFields(patch);

  await db.transaction('rw', db.table(SYNC_TABLE_MAP[table]), db.outbox, async () => {
    const existing = (await db.table(SYNC_TABLE_MAP[table]).get(id)) as
      | (Record<string, unknown> & { updatedAt?: string })
      | undefined;

    await db.table(SYNC_TABLE_MAP[table]).update(id, {
      ...payload,
      updatedAt: new Date().toISOString(),
      _pending: 1,
    });
    await enqueue({
      table,
      op: 'update',
      id,
      data: payload,
      // What we believed the row was when the edit started. The server compares
      // it and reports a conflict rather than overwriting a newer version.
      baseUpdatedAt: existing?.updatedAt ?? null,
    });
  });

  await refreshCounters();
  requestSync();
  return { id };
}

/**
 * Delete a row. Locally it goes immediately (the user asked for it to be gone);
 * on the server it becomes a tombstone, never a physical delete.
 */
export async function deleteRow(table: SyncTable, id: string): Promise<void> {
  await db.transaction('rw', db.table(SYNC_TABLE_MAP[table]), db.outbox, async () => {
    await db.table(SYNC_TABLE_MAP[table]).delete(id);
    await enqueue({ table, op: 'delete', id });
  });

  await refreshCounters();
  requestSync();
}

/**
 * Deactivate instead of delete - the default for anything a ScanEvent might
 * reference. Master data that has ever been used is never removed (FR-LOC-05).
 */
export function deactivateRow(table: SyncTable, id: string): Promise<MutationResult> {
  return updateRow(table, id, { isActive: false });
}

export function reactivateRow(table: SyncTable, id: string): Promise<MutationResult> {
  return updateRow(table, id, { isActive: true });
}
